/**
 * Shared department-filter helpers.
 *
 * Background: the seed data has 30+ departments mixing shop-floor with
 * back-office (Board of Directors, Executive Office, HR Admin, Sales,
 * Procurement, Legal, etc.). Most shop-floor workflows (Daily Shift
 * Log, Worker Allocation, Daily Attendance, Maintenance requests, Work
 * Orders, Manpower Norms) only want gate-to-gate departments — picking
 * "Plant Management" for a Carding allocation makes no sense and just
 * pollutes the dropdown.
 *
 * Single source of truth for the set + the canonical process-flow sort.
 */

/** Shop-floor department types — gate-to-gate (raw cotton → finished yarn). */
export const SHOP_FLOOR_TYPES = new Set([
  'STORES',        // Raw cotton + FG + spare warehouses
  'PREPARATORY',   // Blow Room → Carding → Drawing → Combing → Simplex
  'SPINNING',      // Ring Frame
  'WINDING',       // Autoconer / Link Coner
  'DOUBLING',      // TFO
  'PACKING',       // Final packing + despatch
  'QUALITY',       // QC labs
  'MAINTENANCE',   // Mechanical + Electrical
  'UTILITY',       // Humidification, boiler, compressor
])

/** Process-flow rank — Stores → Preparatory → Spinning → ... → Utility. */
export const SHOP_FLOOR_ORDER: Record<string, number> = {
  STORES: 0,
  PREPARATORY: 1,
  SPINNING: 2,
  WINDING: 3,
  DOUBLING: 4,
  PACKING: 5,
  QUALITY: 6,
  MAINTENANCE: 7,
  UTILITY: 8,
}

/**
 * Filter a list of departments to shop-floor types only, sorted by
 * process flow then by name. Pass any dept shape — the helper reads
 * `department_type` for filtering and `name`/`department_name` for
 * the secondary alphabetical sort.
 */
interface DeptShape {
  department_type?: string | null
  name?: string
  department_name?: string
}

export function filterShopFloorDepts<T>(depts: readonly T[]): T[] {
  return [...depts]
    .filter((d) => {
      const dt = (d as DeptShape).department_type
      return SHOP_FLOOR_TYPES.has(String(dt ?? '').toUpperCase())
    })
    .sort((a, b) => {
      const at = (a as DeptShape).department_type
      const bt = (b as DeptShape).department_type
      const ai = SHOP_FLOOR_ORDER[String(at ?? '').toUpperCase()] ?? 99
      const bi = SHOP_FLOOR_ORDER[String(bt ?? '').toUpperCase()] ?? 99
      if (ai !== bi) return ai - bi
      const an = String((a as DeptShape).name ?? (a as DeptShape).department_name ?? '')
      const bn = String((b as DeptShape).name ?? (b as DeptShape).department_name ?? '')
      return an.localeCompare(bn)
    })
}
